import {
  CheckmarkOutline18Icon,
  CloseOutline18Icon,
} from "@vygruppen/spor-icon-react";
import {
  CustomReserveVehicleModalList,
  filterLogsAfterLastUse,
  filterLogsDuplicates,
  HoverFilterChip,
  mapReserveAlternativeTransportToOption,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleReplacement/utils";
import DashedLine from "features/CenterContent/VehicleDetails/shared/DashedLine";
import { FC, useState } from "react";
import { StaticInfoMessage } from "shared/components/feedback/InfoMessage/StaticInfoMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import {
  ExpandableAlert,
  Flex,
  HStack,
  SearchInput,
  Stack,
  Text,
  VStack,
} from "shared/components/Spor";
import { GetReserveAlternativeTransportResponse } from "shared/types/alternativeTransport";
import { sortReserveTransport } from "shared/utils/alternativeTransport";

type Props = {
  reserveVehicles: GetReserveAlternativeTransportResponse | undefined;
  isReserveLoading: boolean;
  isReserveError: boolean;
  selectedReserveId: string;
  setSelectedReserveId: (reserveId: string) => void;
};

export const SelectReserveVehicleDropdown: FC<Props> = ({
  reserveVehicles,
  isReserveLoading,
  isReserveError,
  selectedReserveId,
  setSelectedReserveId,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const reserveVehicleOptions =
    reserveVehicles?.vehicles
      ?.sort(sortReserveTransport)
      ?.map(mapReserveAlternativeTransportToOption) ?? [];

  const filteredOptions = reserveVehicleOptions.filter((option) => {
    const query = searchQuery.toLowerCase();
    return (
      option?.headingInfo?.toLowerCase().includes(query) ||
      option?.operatorInformationText?.toLowerCase().includes(query) ||
      option?.workShiftId?.toLowerCase().includes(query) ||
      option?.operatorName?.toLowerCase().includes(query)
    );
  });
  const handleItemClick = (value: string) => {
    setSelectedReserveId(value === selectedReserveId ? "" : value);
  };

  if (isReserveLoading) {
    return <SkeletonLoader skeletonType="selectReserveDropdown" />;
  }

  if (isReserveError) {
    return (
      <StaticInfoMessage
        semantics="alarm"
        summary="Kunne ikke hente tilgjengelige reservebusser. Prøv igjen, eller kontakt IT hvis feilen vedvarer."
        style={{ boxShadow: "none" }}
      />
    );
  }

  const getShiftNumber = (logEntry: string): string => {
    const match = logEntry.match(/skiftnr\s+\d+(?:-\d+)?/i);
    if (match && match[0]) {
      return `Brukes for ${match[0]}`;
    }
    return logEntry;
  };

  return (
    <Stack alignItems="center" gap={0}>
      {reserveVehicleOptions.length > 10 && (
        <Stack width="100%" padding="12px">
          <Text>Søk på tognr, stasjon, linjer eller sett-ID</Text>
          <Stack>
            <SearchInput
              value={searchQuery}
              label="Søk..."
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Stack>
        </Stack>
      )}

      {filteredOptions.map((option, idx) => {
        const isSelected = selectedReserveId === option.value;

        return (
          <CustomReserveVehicleModalList
            key={option.value}
            checked={isSelected}
          >
            <HStack display="flex" justifyContent="space-between" mt="12px">
              <VStack alignItems="start" gap="6px" mt="6px">
                <Text variant="sm" fontWeight="bold">
                  {option.headingInfo}
                </Text>
                <Text variant="sm">{option.workShiftId}</Text>
                <Text variant="sm" color="text.highlight">
                  {option.operatorInformationText}
                </Text>
              </VStack>
              <HoverFilterChip
                className="choice-chip"
                checked={isSelected}
                icon={{
                  default: <CheckmarkOutline18Icon />,
                  checked: <CloseOutline18Icon />,
                }}
                onCheckedChange={() => handleItemClick(option.value)}
                mb="auto"
              >
                {isSelected ? ` Fjern` : "Velg"}
              </HoverFilterChip>
            </HStack>
            {option.logs && option.logs.length > 0 && (
              <Flex flexDirection="column" my="6px" gap="12px">
                {filterLogsAfterLastUse(filterLogsDuplicates(option.logs)).map(
                  (logEntry) => (
                    <ExpandableAlert
                      key={`${option.value}-${logEntry}`}
                      title={getShiftNumber(logEntry)}
                      data-color="notice"
                    >
                      <Text>{logEntry}</Text>
                    </ExpandableAlert>
                  ),
                )}
              </Flex>
            )}
            {/* Last element should not have divider */}
            {idx < filteredOptions.length - 1 ? (
              <DashedLine
                variant="colorOutlineMain"
                invert
                height="1px"
                strokeWidth="1"
                mt="6px"
                mb="1px"
                width="calc(100% + 24px)"
                mx="-12px"
                strokeDasharray="1 4"
              />
            ) : (
              <Flex mb="12px" />
            )}
          </CustomReserveVehicleModalList>
        );
      })}
    </Stack>
  );
};
